<template>
  <div class="shipment-tracking">
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x text-muted"></i>
      <p class="text-muted mt-2 small">Consultando estado del envío...</p>
    </div>

    <div v-else-if="!shipment" class="text-center py-3">
      <i class="fas fa-box-open text-muted fs-4"></i>
      <p class="text-muted mt-2 small mb-0">
        {{ noShipmentMessage || 'Este pedido no tiene envío registrado.' }}
      </p>
    </div>

    <div v-else>
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <span class="badge" :class="statusBadgeClass">
            <i :class="statusIcon" class="me-1"></i>
            {{ shipment.status_display }}
          </span>
          <span v-if="shipment.delivery_type_display" class="badge bg-secondary ms-1">
            {{ shipment.delivery_type_display }}
          </span>
        </div>
        <button
          v-if="shipment.tracking_number"
          class="btn btn-sm btn-outline-primary"
          @click="refreshTracking"
          :disabled="refreshing"
        >
          <i :class="refreshing ? 'fas fa-spinner fa-spin' : 'fas fa-sync-alt'" class="me-1"></i>
          Actualizar
        </button>
      </div>

      <div v-if="shipment.tracking_number" class="mb-3">
        <small class="text-muted d-block">Número de seguimiento</small>
        <code class="text-dark fs-6">{{ shipment.tracking_number }}</code>
      </div>

      <div class="row g-2 mb-3">
        <div v-if="shipment.service_type" class="col-6">
          <small class="text-muted d-block">Servicio</small>
          <span class="small text-white">{{ shipment.service_type }}</span>
        </div>
        <div v-if="shipment.quoted_price > 0" class="col-6">
          <small class="text-muted d-block">Costo</small>
          <span class="small text-white">${{ Number(shipment.quoted_price).toLocaleString('es-AR') }}</span>
        </div>
        <div v-if="shipment.estimated_days_min || shipment.estimated_days_max" class="col-6">
          <small class="text-muted d-block">Tiempo estimado</small>
          <span class="small text-white">
            {{ shipment.estimated_days_min }}-{{ shipment.estimated_days_max }} días hábiles
          </span>
        </div>
      </div>

      <div v-if="trackingData && trackingData.history && trackingData.history.length > 0" class="tracking-history mt-3">
        <h6 class="mb-3">
          <i class="fas fa-history me-2"></i>Historial de envío
        </h6>
        <div class="timeline">
          <div
            v-for="(event, index) in trackingData.history"
            :key="index"
            class="timeline-item"
            :class="{ 'active': index === 0 }"
          >
            <div class="timeline-marker">
              <i :class="index === 0 ? 'fas fa-circle text-primary' : 'fas fa-circle text-muted'" style="font-size: 0.5rem;"></i>
            </div>
            <div class="timeline-content">
              <div class="d-flex justify-content-between align-items-start">
                <strong class="small">{{ event.status }}</strong>
                <small class="text-muted">{{ formatDate(event.date) }}</small>
              </div>
              <p v-if="event.description" class="text-muted small mb-0 mt-1">{{ event.description }}</p>
              <small v-if="event.location" class="text-muted">
                <i class="fas fa-map-marker-alt me-1"></i>{{ event.location }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger mt-3 small">
        <i class="fas fa-exclamation-circle me-1"></i>{{ error }}
      </div>

      <div v-if="shipment.last_error" class="alert alert-warning mt-2 small">
        <i class="fas fa-exclamation-triangle me-1"></i>{{ shipment.last_error }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, defineProps } from 'vue';
import { getShipmentTracking, getOrderShipment } from '@/services/shipping';

// eslint-disable-next-line no-undef
const props = defineProps({
  orderId: { type: [Number, String], default: null },
  shipmentId: { type: [Number, String], default: null },
  noShipmentMessage: { type: String, default: '' },
});

const loading = ref(true);
const refreshing = ref(false);
const shipment = ref(null);
const trackingData = ref(null);
const error = ref('');

const statusBadgeClass = computed(() => {
  const map = {
    pending: 'bg-secondary',
    imported: 'bg-info',
    in_transit: 'bg-primary',
    delivered: 'bg-success',
    returned: 'bg-warning',
    cancelled: 'bg-danger',
    error: 'bg-danger',
  };
  return map[shipment.value?.status] || 'bg-secondary';
});

const statusIcon = computed(() => {
  const map = {
    pending: 'fas fa-clock',
    imported: 'fas fa-file-import',
    in_transit: 'fas fa-truck',
    delivered: 'fas fa-check-circle',
    returned: 'fas fa-undo',
    cancelled: 'fas fa-times-circle',
    error: 'fas fa-exclamation-circle',
  };
  return map[shipment.value?.status] || 'fas fa-box';
});

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const loadShipment = async () => {
  try {
    if (props.orderId) {
      const data = await getOrderShipment(props.orderId);
      if (data) {
        shipment.value = data;
        if (data.tracking_number && data.id) {
          await loadTracking(data.id);
        }
      }
    }
  } catch (e) {
    console.error('Error loading shipment:', e);
  } finally {
    loading.value = false;
  }
};

const loadTracking = async (id) => {
  try {
    const result = await getShipmentTracking(id);
    if (result.shipment) {
      shipment.value = result.shipment;
    }
    trackingData.value = result.tracking;
    if (result.error) {
      error.value = result.error;
    }
  } catch (e) {
    console.error('Error loading tracking:', e);
    error.value = 'Error al consultar el tracking';
  }
};

const refreshTracking = async () => {
  refreshing.value = true;
  error.value = '';
  try {
    if (shipment.value?.id) {
      await loadTracking(shipment.value.id);
    }
  } finally {
    refreshing.value = false;
  }
};

onMounted(loadShipment);
</script>

<style scoped>
.timeline {
  position: relative;
  padding-left: 1.5rem;
}

.timeline::before {
  content: '';
  position: absolute;
  left: 0.35rem;
  top: 0.5rem;
  bottom: 0.5rem;
  width: 1px;
  background: var(--border-color);
}

.timeline-item {
  position: relative;
  padding-bottom: 1rem;
  padding-left: 0.5rem;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: -1.25rem;
  top: 0.15rem;
}

.timeline-content {
  padding: 0.5rem;
  background: var(--bg-secondary);
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-light);
}

.timeline-item.active .timeline-content {
  background: rgba(var(--primary-color-rgb), 0.06);
  border-color: rgba(var(--primary-color-rgb), 0.2);
}
</style>
